// Services Map · tab Service (Mint-ServicesMap `svcs`, `svcGroups`, `svcKpis`): trạng thái lịch, rotation, lọc.
export type ServiceGroupKey = 'nea' | 'sea' | 'isc' | 'eu' | 'am' | 'oc';

/** Một service (TblServiceRoute): hãng và rotation cách nhau bằng dấu cách. */
export interface BoardService {
  code: string;
  name: string;
  grp: ServiceGroupKey;
  carriers: string;
  rotation: string;
  /** vòng quay, ngày */
  circ: number;
  vn: string;
  main: string;
  tt: number;
  last: string;
  locked: boolean;
  active: boolean;
  bound: string;
  link: string;
}

export const SERVICE_GROUPS: readonly { key: ServiceGroupKey; label: string; short: string }[] = [
  { key: 'nea', label: 'Nội Á · Đông Bắc Á', short: 'Đông Bắc Á' },
  { key: 'sea', label: 'Nội Á · Đông Nam Á', short: 'Đông Nam Á' },
  { key: 'isc', label: 'Ấn Độ – Trung Đông', short: 'Ấn – Trung Đông' },
  { key: 'eu', label: 'Âu – Địa Trung Hải', short: 'Âu – ĐTH' },
  { key: 'am', label: 'Mỹ – Mỹ Latin', short: 'Mỹ' },
  { key: 'oc', label: 'Úc – Thái Bình Dương', short: 'Úc – TBD' },
];

export type ScheduleStatus = 'ok' | 'due' | 'end' | 'off';
export const SCHEDULE_STATUS: Record<ScheduleStatus, string> = { ok: 'Đủ lịch', due: 'Sắp hết lịch', end: 'Hết lịch', off: 'Ngừng chạy' };

export interface ServiceRow extends BoardService {
  cars: string[];
  rot: string[];
  /** số tuần đã hết lịch / có blank sailing trong 8 tuần tới (bỏ tuần hiện tại) */
  ends: number;
  blank: number;
  status: ScheduleStatus;
  ships: number;
  /** chuyến có lịch trong 8 tuần tới (chuyến thêm trong tuần tính 2) */
  trips: number;
}

export const isVnPort = (p: string) => p.startsWith('VN');

export function serviceRow(s: BoardService, cells: readonly string[]): ServiceRow {
  const fut = cells.slice(1);
  const ends = fut.filter((x) => x === '>').length;
  const blank = fut.filter((x) => x === '').length;
  const status: ScheduleStatus = !s.active ? 'off' : ends >= 3 || fut.every((x) => x === '>' || x === '-') ? 'end' : ends > 0 ? 'due' : 'ok';
  const trips = fut.filter((x) => x && x !== '-' && x !== '>').reduce((n, x) => n + (x.includes('|+') ? 2 : 1), 0);
  return { ...s, cars: s.carriers ? s.carriers.split(' ') : [], rot: s.rotation ? s.rotation.split(' ') : [], ends, blank, status, ships: Math.max(1, Math.round(s.circ / 7)), trips };
}

export const needsSchedule = (s: ServiceRow) => s.status === 'due' || s.status === 'end';

export const dedupe = <T,>(a: readonly T[]): T[] => a.filter((p, i) => p !== a[i - 1]);

export interface RotDot {
  port: string;
  title: string;
  vn: boolean;
  label: boolean;
  /** nhãn cảng VN giữa vòng đặt phía trên chấm */
  above: boolean;
  line: boolean;
}

/** Rotation rút gọn (≤ 14 cảng giữ hết): chỉ ghi nhãn cảng đầu, cuối và cảng VN; cảng giữa bị bỏ thì ghi "còn n cảng". */
export function rotationDots(s: Pick<ServiceRow, 'rot' | 'vn'>): RotDot[] {
  const rot = dedupe(s.rot);
  const found = rot.indexOf(s.vn);
  const vi = found >= 0 ? found : rot.findIndex(isVnPort);
  const keep =
    rot.length <= 14
      ? rot.map((_, i) => i)
      : [...new Set([0, 1, 2, 3, 4, 5, vi - 1, vi, vi + 1, rot.length - 5, rot.length - 4, rot.length - 3, rot.length - 2, rot.length - 1].filter((x) => x >= 0 && x < rot.length))].sort((a, b) => a - b);
  return keep.map((ix, n) => {
    const port = rot[ix] as string;
    const vn = isVnPort(port);
    const next = keep[n + 1];
    const gap = next !== undefined && next - ix > 1;
    return {
      port,
      title: `${ix + 1}. ${port}${vn ? ' · cảng VN' : ''}${gap ? ` · còn ${(next as number) - ix - 1} cảng` : ''}`,
      vn,
      label: ix === 0 || ix === rot.length - 1 || ix === vi,
      above: ix === vi && ix !== 0 && ix !== rot.length - 1,
      line: n < keep.length - 1,
    };
  });
}

export type QuickTab = 'all' | 'due' | 'blank' | 'off';
export const QUICK_TABS: readonly { key: QuickTab; label: string; tip: string; test: (s: ServiceRow) => boolean }[] = [
  { key: 'all', label: 'Tất cả', tip: 'Tất cả service', test: () => true },
  { key: 'due', label: 'Cần lịch', tip: 'Cần nhập lịch dài hạn (hết / sắp hết)', test: needsSchedule },
  { key: 'blank', label: 'Blank', tip: 'Có blank sailing trong 8 tuần', test: (s) => s.blank > 0 },
  { key: 'off', label: 'Ngừng', tip: 'Ngừng chạy', test: (s) => !s.active },
];

export interface ServiceFilter {
  grp?: ServiceGroupKey | '';
  vn?: string;
  car?: string;
  q?: string;
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

/** `skip` = bỏ một điều kiện để đếm số theo từng giá trị của chính ô lọc đó. */
export function passService(s: ServiceRow, f: ServiceFilter, skip?: 'grp' | 'vn' | 'car'): boolean {
  if (skip !== 'grp' && f.grp && s.grp !== f.grp) return false;
  if (skip !== 'car' && f.car && !s.cars.includes(f.car)) return false;
  if (skip !== 'vn' && f.vn && !s.rot.includes(f.vn)) return false;
  const q = f.q?.trim();
  return !q || fold([s.code, s.name, s.carriers, s.rotation].join(' ')).includes(fold(q));
}

export const VN_PORT_OPTIONS: readonly [string, string][] = [
  ['VNSGN', 'VNSGN · Cát Lái'],
  ['VNVUT', 'VNVUT · Cái Mép'],
  ['VNHPH', 'VNHPH · Hải Phòng'],
  ['VNDAD', 'VNDAD · Đà Nẵng'],
];

export interface ServiceKpi {
  key: 'run' | 'trips' | 'blank' | 'due';
  label: string;
  value: number;
  sub: string;
}

export function serviceKpis(rows: readonly ServiceRow[]): ServiceKpi[] {
  const blank = rows.reduce((n, s) => n + (s.active ? s.blank : 0), 0);
  const due = rows.filter(needsSchedule).length;
  return [
    { key: 'run', label: 'Service chạy', value: rows.filter((s) => s.active).length, sub: `trên ${rows.length} service đã khai` },
    { key: 'trips', label: 'Chuyến 8 tuần', value: rows.reduce((n, s) => n + s.trips, 0), sub: 'tại cảng VN · chiều xuất' },
    { key: 'blank', label: 'Blank sailing', value: blank, sub: 'tuần trống trong 8 tuần' },
    { key: 'due', label: 'Cần nhập lịch', value: due, sub: 'hết hoặc sắp hết lịch dài hạn' },
  ];
}
